import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CheckSquare, Clock, AlertTriangle, CheckCircle2, ListTodo, FileText, TrendingUp, IndianRupee, Users, Shield, Filter, Calendar } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { getTaskStats, getQuotationStats, getUserAnalytics } from '../../services/taskApi';

const TIMEFRAMES = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'weekly', label: 'Last 7 Days' },
  { value: 'monthly', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const TaskDashboard = () => {
  const { user, isDark } = useOutletContext();
  const [taskStats, setTaskStats] = useState(null);
  const [quotationStats, setQuotationStats] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [timeframe, setTimeframe] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const promises = [getTaskStats()];
      if (user.role === 'admin' || user.permissions?.quotations) promises.push(getQuotationStats());
      else promises.push(Promise.resolve({ data: null }));
      if (user.role === 'admin') {
        const params = {};
        if (timeframe === 'custom' && customFrom && customTo) {
          params.date_from = customFrom;
          params.date_to = customTo;
        } else if (timeframe && timeframe !== 'custom') {
          params.timeframe = timeframe;
        }
        promises.push(getUserAnalytics(params));
      } else promises.push(Promise.resolve({ data: [] }));
      const [ts, qs, ua] = await Promise.all(promises);
      setTaskStats(ts.data);
      if (qs.data) setQuotationStats(qs.data);
      if (ua.data) setUserAnalytics(ua.data);
    } catch (err) {
      console.error('TaskDashboard: stats fetch failed', err);
    }
  }, [user, timeframe, customFrom, customTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cardCls = isDark ? 'border border-white/5 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/10' : 'border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/8' : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50';

  const taskCards = taskStats ? [
    { label: 'Total', value: taskStats.total, icon: CheckSquare, color: 'text-blue-400', bg: 'bg-blue-500/10', ring: 'ring-blue-500/20' },
    { label: 'To Do', value: taskStats.todo, icon: ListTodo, color: isDark ? 'text-slate-300' : 'text-slate-500', bg: 'bg-slate-500/10', ring: 'ring-slate-500/20' },
    { label: 'In Progress', value: taskStats.in_progress, icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10', ring: 'ring-amber-500/20' },
    { label: 'Pending Review', value: taskStats.pending_review || 0, icon: Shield, color: 'text-violet-400', bg: 'bg-violet-500/10', ring: 'ring-violet-500/20' },
    { label: 'Completed', value: taskStats.done, icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', ring: 'ring-emerald-500/20' },
    { label: 'Overdue', value: taskStats.overdue, icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', ring: 'ring-red-500/20' },
  ] : [];

  return (
    <div className="space-y-8" data-testid="task-dashboard">
      <div className="flex items-end justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Welcome, {user.name}</h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Here&apos;s your overview for today</p>
        </div>
      </div>

      {user.permissions?.tasks !== false && (
        <div>
          <h2 className={`text-xs font-semibold mb-4 uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Task Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {taskCards.map(c => (
              <Card key={c.label} className={`${cardCls} transition-all duration-200`}>
                <CardContent className="p-4">
                  <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center mb-3`}>
                    <c.icon size={18} className={c.color} />
                  </div>
                  <p className={`text-2xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.value}</p>
                  <p className={`text-[11px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{c.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {quotationStats && (
        <div>
          <h2 className={`text-xs font-semibold mb-4 uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Quotation Pipeline</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className={cardCls}><CardContent className="p-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-3"><FileText size={18} className="text-indigo-400" /></div>
              <p className={`text-2xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{quotationStats.total}</p><p className={`text-[11px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Total Quotations</p>
            </CardContent></Card>
            <Card className={cardCls}><CardContent className="p-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-3"><TrendingUp size={18} className="text-emerald-400" /></div>
              <p className={`text-2xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{quotationStats.converted}</p><p className={`text-[11px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Converted</p>
            </CardContent></Card>
            <Card className={cardCls}><CardContent className="p-4">
              <p className="text-3xl font-bold text-emerald-400 tabular-nums">{quotationStats.conversion_rate}%</p><p className={`text-[11px] mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Conversion Rate</p>
            </CardContent></Card>
            <Card className={cardCls}><CardContent className="p-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center mb-3"><IndianRupee size={18} className="text-amber-400" /></div>
              <p className={`text-2xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>Rs.{(quotationStats.total_value/1000).toFixed(0)}K</p><p className={`text-[11px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Pipeline Value</p>
            </CardContent></Card>
          </div>
          <div className="grid grid-cols-5 gap-2 mt-3">
            {[
              { key: 'draft', color: isDark ? 'text-slate-400' : 'text-slate-600' },
              { key: 'sent', color: 'text-blue-400' },
              { key: 'approved', color: 'text-emerald-400' },
              { key: 'converted', color: 'text-green-400' },
              { key: 'rejected', color: 'text-red-400' },
            ].map(s => (
              <div key={s.key} className={`rounded-lg p-3 text-center ${isDark ? 'bg-white/[0.03] border border-white/5' : 'bg-white border border-gray-200 shadow-sm'}`}>
                <p className={`text-lg font-bold ${s.color} tabular-nums`}>{quotationStats[s.key]}</p>
                <p className={`text-[10px] capitalize mt-0.5 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{s.key}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {user.role === 'admin' && (
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
            <h2 className={`text-xs font-semibold uppercase tracking-widest flex items-center gap-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              <Users size={14} /> Team Performance
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={14} className={isDark ? 'text-white/30' : 'text-gray-400'} />
              <select
                className={`h-9 rounded-lg text-xs px-3 transition-colors ${selectCls}`}
                value={timeframe}
                onChange={e => setTimeframe(e.target.value)}
                data-testid="analytics-timeframe-filter"
              >
                {TIMEFRAMES.map(tf => (
                  <option key={tf.value} value={tf.value}>{tf.label}</option>
                ))}
              </select>
              {timeframe === 'custom' && (
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-1.5 rounded-lg px-2 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-300'}`}>
                    <Calendar size={12} className={isDark ? 'text-white/30' : 'text-gray-400'} />
                    <Input
                      type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                      className={`bg-transparent border-0 text-xs h-9 w-32 p-0 focus-visible:ring-0 ${isDark ? 'text-white' : 'text-gray-900'}`}
                      data-testid="analytics-date-from"
                    />
                  </div>
                  <span className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>to</span>
                  <div className={`flex items-center gap-1.5 rounded-lg px-2 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-300'}`}>
                    <Calendar size={12} className={isDark ? 'text-white/30' : 'text-gray-400'} />
                    <Input
                      type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                      className={`bg-transparent border-0 text-xs h-9 w-32 p-0 focus-visible:ring-0 ${isDark ? 'text-white' : 'text-gray-900'}`}
                      data-testid="analytics-date-to"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {userAnalytics.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {userAnalytics.map(u => (
                <Card key={u.user_id} className={`${cardCls} transition-all`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm ${
                        isDark ? 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 text-blue-400 border border-blue-500/10' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.name}</p>
                        <p className={`text-[10px] capitalize ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{u.role}</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className={`text-lg font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.completion_pct}%</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-200'}`}>
                        <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${u.completion_pct}%`, background: 'linear-gradient(90deg, #3b82f6, #10b981)' }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-1.5 text-center">
                      {[
                        { val: u.assigned, label: 'Assigned', color: isDark ? 'text-white' : 'text-gray-900' },
                        { val: u.completed, label: 'Done', color: 'text-emerald-400' },
                        { val: u.in_progress, label: 'Active', color: 'text-amber-400' },
                        { val: u.pending_review || 0, label: 'Review', color: 'text-violet-400' },
                        { val: u.overdue, label: 'Overdue', color: 'text-red-400' },
                      ].map(s => (
                        <div key={s.label} className={`rounded-lg p-2 ${isDark ? 'bg-white/[0.03] border border-white/5' : 'bg-gray-50 border border-gray-200'}`}>
                          <p className={`text-sm font-bold ${s.color} tabular-nums`}>{s.val}</p>
                          <p className={`text-[8px] mt-0.5 ${isDark ? 'text-white/25' : 'text-gray-400'}`}>{s.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className={`text-center py-12 text-sm ${isDark ? 'text-white/20' : 'text-gray-400'}`}>
              <Users size={32} className="mx-auto mb-3 opacity-30" />
              No analytics data {timeframe === 'custom' ? 'for selected range' : timeframe ? `for ${TIMEFRAMES.find(t => t.value === timeframe)?.label}` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskDashboard;
